import { LocationContact } from '@hakimo-ui/hakimo/types';
import { eventTracker, toast } from '@hakimo-ui/hakimo/util';
import { Button, Modal } from '@hakimo-ui/shared/ui-base';
import { ChatBubbleLeftIcon } from '@heroicons/react/24/solid';
import { useMemo, useState } from 'react';
import { useFileHandler } from '../../sop-workflow/util';
import { MAX_SMS_LENGTH } from './constants';
import { MessageField } from './MessageField';
import {
  attachmentState,
  canReceiveSms,
  sendLabel,
  smsAlerts,
  visibleLength,
} from './sms-util';
import { SMSAttachments } from './SMSAttachments';
import { SMSRecipients } from './SMSRecipients';
import { useSMSFanout } from './useSMSFanout';

interface Props {
  contact: LocationContact;
  selectableContacts: LocationContact[];
  locationId?: number;
  locationAlarmId?: string;
  alarmGroupId?: string;
  isScan?: boolean;
}

export function ContactSMS(props: Props) {
  const {
    contact,
    selectableContacts,
    locationId,
    locationAlarmId,
    alarmGroupId,
    isScan,
  } = props;
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [message, setMessage] = useState('');
  const [extraRecipients, setExtraRecipients] = useState<LocationContact[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const { fileList, previewList, onFilesSelect, onFileRemove, setFiles } =
    useFileHandler({ multiple: true });
  const { sending, sent, failures, send, reset } = useSMSFanout();

  const recipients = useMemo(
    () => [contact, ...extraRecipients].filter(canReceiveSms),
    [contact, extraRecipients]
  );

  const targets = recipients.filter((r) => !sent.has(String(r.id)));
  const { overBytes, overImages } = attachmentState(fileList);
  const alerts = smsAlerts(failures, fileList, fileError);

  const onOpen = () => setShowSMSModal(true);

  const onClose = () => setShowSMSModal(false);

  const clearDraft = () => {
    setMessage('');
    setExtraRecipients([]);
    setFiles([]);
    setFileError(null);
    reset();
  };

  const onChange = (val: string) => {
    if (visibleLength(val) <= MAX_SMS_LENGTH) setMessage(val);
  };

  const onSend = async () => {
    const ok = await send(targets, {
      message,
      fileList,
      locationId,
      locationAlarmId,
      alarmGroupId,
      isScan,
    });
    if (ok) {
      toast('Message sent successfully', { type: 'success' });
      clearDraft();
      setShowSMSModal(false);
    }
  };

  const isSendDisabled =
    targets.length === 0 ||
    (fileList.length === 0 && message.trim() === '') ||
    overBytes ||
    overImages;

  const actions = (
    <>
      <Button
        variant="primary"
        onClick={onSend}
        loading={sending}
        disabled={isSendDisabled || sending}
        onSideEffect={eventTracker('sms_location_contact')}
      >
        {sendLabel(failures.length > 0, targets.length)}
      </Button>
      <Button variant="outline" onClick={onClose} disabled={sending}>
        Close
      </Button>
    </>
  );

  return (
    <>
      <Button variant="icon" onClick={onOpen} title={`Message ${contact.name}`}>
        <ChatBubbleLeftIcon className="text-primary-500 h-4 w-4" />
      </Button>
      {showSMSModal && (
        <Modal
          open={true}
          onClose={onClose}
          title="Message"
          footer={actions}
          closable={!sending}
          disableBackdropClose={sending}
        >
          <div className="w-[30rem] p-4">
            <SMSRecipients
              primary={contact}
              selectableContacts={selectableContacts}
              extraRecipients={extraRecipients}
              alerts={alerts}
              onChange={setExtraRecipients}
            />
            <SMSAttachments
              fileList={fileList}
              previewList={previewList}
              onFilesSelect={onFilesSelect}
              onFileRemove={onFileRemove}
              setFiles={setFiles}
              onFileError={setFileError}
            >
              <MessageField value={message} onChange={onChange} />
            </SMSAttachments>
          </div>
        </Modal>
      )}
    </>
  );
}

export default ContactSMS;
